import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import AmbientBackground from '../common/AmbientBackground';
import Button from '../common/Button';
import { supabase } from '../../config/supabase';

interface TarotCard {
  id: string;
  card_name: string;
  arcana_type: string;
  suit: string | null;
  image_url: string | null;
  arcana_info: string | null;
  themes: string[] | null;
  shadow_themes: string[] | null;
}

// Define the order for Major Arcana cards
const majorArcanaOrder = [
  'The Fool',
  'The Magician',
  'The High Priestess',
  'The Empress',
  'The Emperor',
  'The Hierophant',
  'The Lovers',
  'The Chariot',
  'Strength',
  'The Hermit',
  'Wheel of Fortune',
  'Justice',
  'The Hanged Man',
  'Death',
  'Temperance',
  'The Devil',
  'The Tower',
  'The Star',
  'The Moon',
  'The Sun',
  'Judgement',
  'The World'
];

// Define suit order
const suitOrder = ['Wands', 'Cups', 'Swords', 'Pentacles'];

// Define card rank order within suits
const rankOrder = ['Ace', 'King', 'Queen', 'Page', 'Ten', 'Nine', 'Eight', 'Seven', 'Six', 'Five', 'Four', 'Three', 'Two'];

const DeckViewer: React.FC = () => {
  const [cards, setCards] = useState<TarotCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArcana, setSelectedArcana] = useState<'major' | 'minor' | null>(null);
  const [selectedSuit, setSelectedSuit] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<TarotCard | null>(null);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const { data, error } = await supabase
          .from('tarot_cards')
          .select('id, card_name, arcana_type, suit, image_url, arcana_info, themes, shadow_themes')
          .eq('orientation', 'upright');

        if (error) throw error;
        setCards(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch cards');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, []);

  const getArcanaDisplay = (card: TarotCard) => {
    if (!card.arcana_type) return 'Unknown Arcana';
    const arcanaType = card.arcana_type.toLowerCase();
    return arcanaType === 'major'
      ? 'Major Arcana'
      : `Minor Arcana${card.suit ? ` • ${card.suit}` : ''}`;
  };

  // Filter cards based on selected arcana and suit
  const filteredCards = cards.filter(card => {
    if (selectedArcana === null) return true;
    if (selectedArcana === 'major') return card.arcana_type?.toLowerCase() === 'major';
    if (selectedArcana === 'minor') {
      if (selectedSuit) {
        return card.arcana_type?.toLowerCase() === 'minor' && card.suit === selectedSuit;
      }
      return card.arcana_type?.toLowerCase() === 'minor';
    }
    return true;
  });

  // Sort cards based on arcana type, suit, and rank
  const sortedCards = [...filteredCards].sort((a, b) => {
    // If both are Major Arcana, use the defined order
    if (a.arcana_type?.toLowerCase() === 'major' && b.arcana_type?.toLowerCase() === 'major') {
      const indexA = majorArcanaOrder.indexOf(a.card_name);
      const indexB = majorArcanaOrder.indexOf(b.card_name);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
    }

    // If only one is Major Arcana, prioritize it
    if (a.arcana_type?.toLowerCase() === 'major') return -1;
    if (b.arcana_type?.toLowerCase() === 'major') return 1;

    // For Minor Arcana, sort by suit then rank
    if (a.suit && b.suit) {
      // First compare suits
      const suitCompare = suitOrder.indexOf(a.suit) - suitOrder.indexOf(b.suit);
      if (suitCompare !== 0) return suitCompare;

      // Then compare ranks within the same suit
      const getRank = (cardName: string) => {
        const rank = cardName.split(' ')[0];
        return rankOrder.indexOf(rank);
      };
      return getRank(a.card_name) - getRank(b.card_name);
    }

    // Fallback to alphabetical sorting
    return a.card_name.localeCompare(b.card_name);
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-sun" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 dark:text-red-400 p-4">
        Error: {error}
      </div>
    );
  }

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-deep.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment">
            Explore a Virtual Deck
          </h1>
          <div className="space-y-4 text-ink dark:text-parchment/60 mb-6">
            <p>
              Take a spin around the most famously used deck in the world! The Rider-Waite Smith Deck was created in 1909 and most tarot cards decks are build upon it's imagery. Really, though, you can use any deck you'd like.
            </p>
            <p>
              Just filter to see the cards you're interested in. Click the card to learn more. Need to know the basics of reading Tarot? Like, what does "Major or Minor Arcana even mean, for example? Visit the <Link to="/guide/101" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">Tarot 101</Link> page.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Button
                variant={selectedArcana === null ? 'primary' : 'outline'}
                onClick={() => {
                  setSelectedArcana(null);
                  setSelectedSuit(null);
                }}
              >
                All Cards
              </Button>
              <Button
                variant={selectedArcana === 'major' ? 'primary' : 'outline'}
                onClick={() => {
                  setSelectedArcana('major');
                  setSelectedSuit(null);
                }}
              >
                Major Arcana
              </Button>
              <Button
                variant={selectedArcana === 'minor' ? 'primary' : 'outline'}
                onClick={() => {
                  setSelectedArcana('minor');
                  setSelectedSuit(null);
                }}
              >
                Minor Arcana
              </Button>
            </div>

            {selectedArcana === 'minor' && (
              <div className="flex flex-wrap gap-4">
                {suitOrder.map((suit) => (
                  <Button
                    key={suit}
                    variant={selectedSuit === suit ? 'secondary' : 'outline'}
                    onClick={() => setSelectedSuit(selectedSuit === suit ? null : suit)}
                  >
                    {suit}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedCards.map((card) => (
              <div
                key={card.id}
                className="bg-vellum dark:bg-shadow rounded-lg border border-mist dark:border-dusk overflow-hidden hover:shadow-lg transition-shadow"
              >
                {card.image_url ? (
                  <button
                    onClick={() => setSelectedCard(card)}
                    className="w-full aspect-[2/3] relative group"
                  >
                    <img
                      src={card.image_url}
                      alt={card.card_name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-white text-lg">View</span>
                    </div>
                  </button>
                ) : (
                  <div className="w-full aspect-[2/3] bg-vellum dark:bg-dusk flex items-center justify-center">
                    <span className="text-mist dark:text-parchment/70">No image</span>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-lg font-medium text-ink dark:text-parchment">
                    {card.card_name}
                  </h3>
                  <p className="text-xs text-sun dark:text-ember mt-1">
                    {getArcanaDisplay(card)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCard && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedCard(null)}
          >
            <div
              className="relative max-w-4xl w-full bg-vellum dark:bg-shadow rounded-lg shadow-xl overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedCard(null)}
                className="absolute top-4 right-4 text-ink/50 hover:text-ink dark:text-parchment/60 dark:hover:text-parchment/20 z-10"
              >
                <span className="text-2xl leading-none">×</span>
              </button>

              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Card Image */}
                  <div className="md:w-1/3">
                    {selectedCard.image_url && (
                      <img
                        src={selectedCard.image_url}
                        alt={selectedCard.card_name}
                        className="w-full rounded-lg shadow-lg"
                      />
                    )}
                    <div className="mt-4 text-center">
                      <h2 className="text-2xl font-display font-bold text-ink dark:text-parchment">
                        {selectedCard.card_name}
                      </h2>
                      <p className="text-sm text-sun dark:text-ember mt-1">
                        {getArcanaDisplay(selectedCard)}
                      </p>
                    </div>
                  </div>

                  {/* Card Details */}
                  <div className="md:w-2/3 space-y-6">
                    {selectedCard.arcana_info && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment/20 mb-2">
                          Arcana Information
                        </h3>
                        <div className="prose prose-gray dark:prose-invert max-w-none">
                          {selectedCard.arcana_info.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCard.themes && selectedCard.themes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment/20 mb-2">
                          Upright Themes
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedCard.themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full text-sm"
                            >
                              {theme.replace(/[{}]/g, '')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCard.shadow_themes && selectedCard.shadow_themes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment/20 mb-2">
                          Shadow Themes
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedCard.shadow_themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full text-sm"
                            >
                              {theme.replace(/[{}]/g, '')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AmbientBackground>
  );
};

export default DeckViewer;
